import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {handleResponseError} from '../../common/api-helpers';
import {
    completePendingRequestIfExists,
    createPendingRequest,
    PendingRequest
} from '../../common/request-synchronization';
import {UserRole} from '../../enums/user-role';
import {AppConfigService} from '../../services/config.service';
import {AuthService} from '../auth/auth.service';
import {InvEntity, Portfolio} from './inv-portfolio';

export interface InvEntitiesPortfolios {
    invEntities: InvEntity[];
    portfolios: Portfolio[];
}

@Injectable()
export class InvPortfolioService {

    private readonly investmentEntitiesUrl = this.appConfigService.api['investmentEntities'];

    private readonly portfoliosUrl = this.appConfigService.api['portfolios'];

    private cache: InvEntitiesPortfolios;

    private pendingRequest: PendingRequest<InvEntitiesPortfolios>;

    constructor(
        private appConfigService: AppConfigService,
        private httpClient: HttpClient,
        private authService: AuthService
    ) {
        this.listenToAuthorization();
    }

    public getCached(notUndefinedResult: boolean = true): InvEntitiesPortfolios {
        return notUndefinedResult
            ? (
                this.cache
                || {
                    invEntities: [],
                    portfolios:  []
                }
            )
            : this.cache;
    }

    public resolve(): Observable<InvEntitiesPortfolios> {
        let result$: Observable<InvEntitiesPortfolios>;

        if (this.cache) {
            result$ = of(this.cache);
        } else {
            const pendingRequest = this.pendingRequest
                || createPendingRequest(
                    this.fetchInvestmentEntitiesPortfolios(),
                    newPendingRequest => this.pendingRequest = newPendingRequest,
                    () => this.pendingRequest = undefined
                );

            result$ = pendingRequest.request$;
        }

        return result$.pipe(
            tap(data => this.setCache(data))
        );
    }

    public getInvEntity(invEntityId?: number | undefined, dontReturnDefault?: boolean): InvEntity {
        const {invEntities} = this.getCached();

        return invEntityId
            ? invEntities.find(inv => inv.id === invEntityId)
            : dontReturnDefault
                ? undefined
                : invEntities[0];
    }

    public isInvPortfoliosAvailable(): boolean {
        const {invEntities, portfolios} = this.getCached();

        return !!invEntities.length && !!portfolios.length;
    }

    public getPortfolio(invEntityId?: number, portfolioId?: number | undefined): Portfolio | undefined {
        const {portfolios} = this.getCached();

        let portfolio = portfolioId
            && portfolios.find(p =>
                p.id === portfolioId
                && (!invEntityId || p.investmentEntityId === invEntityId)
            );

        portfolio = portfolio
            || (
                invEntityId
                    ? portfolios.find(p => p.investmentEntityId === invEntityId)
                    : undefined
            );

        return portfolio;
    }

    public getDisplayInvPortfolio(
        {investmentEntityId, portfolioId}: {
            investmentEntityId?: number;
            portfolioId?: number;
        }
    ): {invEntity: string, portfolio: string} {
        const getDisplayLabelOrId = (label: string | undefined, id: number) =>
            label || (id ? `ID: ${id}` : '');

        const invEntity = investmentEntityId && this.getInvEntity(investmentEntityId);
        const portfolio = portfolioId && this.getPortfolio(undefined, portfolioId);

        return {
            portfolio: getDisplayLabelOrId(portfolio && portfolio.name, portfolioId),
            invEntity: getDisplayLabelOrId(invEntity && invEntity.code, investmentEntityId)
        };
    }

    private fetchInvestmentEntitiesPortfolios(): Observable<InvEntitiesPortfolios> {
        const invEntities$ = this.investmentEntitiesUrl
            ? this.httpClient.get<InvEntity[]>(this.investmentEntitiesUrl).pipe(
                catchError(() => of(undefined)), // Skip if fail
                map(items => items || [])
            )
            : of([]);

        const portfolios$ = this.portfoliosUrl
            ? this.httpClient.get<Portfolio[]>(this.portfoliosUrl).pipe(
                catchError(() => of(undefined)), // Skip if fail
                map(items => items || [])
            )
            : of([]);

        return forkJoin({
            invEntities: invEntities$,
            portfolios:  portfolios$
        }).pipe(
            catchError(handleResponseError)
        );
    }

    private listenToAuthorization(): void {
        this.authService.userRole$.pipe(
            tap((userRole: UserRole) => {
                if (!userRole) {
                    this.setCache(undefined);
                }
            })
        ).subscribe();
    }

    private setCache(data: InvEntitiesPortfolios | undefined): void {
        this.cache = data;

        completePendingRequestIfExists(
            this.pendingRequest,
            data
        );
    }

}
